import { useStrictLocalization } from "@faire/web/common/localization";
import shuffle from "lodash/shuffle";
import { useCallback, useRef, useState } from "react";

import { setIntervalIfPossible } from "@faire/retailer/lib/async-functions/setIntervalIfPossible";
import { getSuggestedSearchTerms } from "@faire/retailer/serialized-data/getSuggestedSearchTerms";

const CYCLING_SPEED = 5000;

export const useSearchPlaceholder = () => {
  const [placeholderSuggestion, setPlaceholderSuggestion] = useState<
    string | undefined
  >(undefined);
  const [apparelSearchPlaceholders] = useState(
    shuffle(getSuggestedSearchTerms())
  );
  const cycleInterval = useRef<number>();
  const currentIndex = useRef(0);
  const { strictLocalize } = useStrictLocalization();
  const emptySuggestions = apparelSearchPlaceholders.length === 0;
  const placeholder = placeholderSuggestion
    ? placeholderSuggestion
    : strictLocalize({
        defaultMessage: "Search products or brands",
        description: {
          text: "Text for search box.",
        },
      });
  const updatePlaceholderSuggestion = useCallback(() => {
    if (emptySuggestions) {
      return;
    }
    setPlaceholderSuggestion(apparelSearchPlaceholders[currentIndex.current]);
    currentIndex.current =
      (currentIndex.current + 1) % apparelSearchPlaceholders.length;
  }, [apparelSearchPlaceholders, emptySuggestions]);

  const startPlaceholderCycling = useCallback(() => {
    if (cycleInterval.current || emptySuggestions) {
      return;
    }

    updatePlaceholderSuggestion();
    cycleInterval.current = setIntervalIfPossible(
      updatePlaceholderSuggestion,
      CYCLING_SPEED
    );
  }, [emptySuggestions, updatePlaceholderSuggestion]);

  const stopPlaceholderCycling = useCallback(() => {
    if (cycleInterval.current) {
      clearInterval(cycleInterval.current);
      setPlaceholderSuggestion(undefined);
      cycleInterval.current = undefined;
    }
  }, []);

  return {
    placeholder,
    startPlaceholderCycling,
    stopPlaceholderCycling,
  };
};
